// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminSecurity\AdminSecurityAlerts.js
// Версия: 2.0.0 - Light theme, real data
// Дата: 04.12.2025

import React, { useState, useEffect } from 'react';
import './AdminSecurityAlerts.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const SEVERITY_LABELS = {
  low: { label: 'Низкий', icon: '🟢', color: 'green' },
  medium: { label: 'Средний', icon: '🟡', color: 'orange' },
  warning: { label: 'Внимание', icon: '🟠', color: 'orange' },
  high: { label: 'Высокий', icon: '🔴', color: 'red' },
  critical: { label: 'Критический', icon: '⛔', color: 'red' }
};

const TYPE_LABELS = {
  low_balance: { label: 'Низкий баланс', icon: '💰' },
  failed_login: { label: 'Неудачный вход', icon: '🔐' },
  large_withdrawal: { label: 'Крупный вывод', icon: '💸' },
  suspicious_activity: { label: 'Подозрительно', icon: '⚠️' },
  rate_limit: { label: 'Лимит запросов', icon: '⚡' },
  new_device: { label: 'Новое устройство', icon: '📱' }
};

const AdminSecurityAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => { fetchAlerts(); }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/security/alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setAlerts(data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resolveAlert = async (id) => {
    const action = prompt('Действие (опционально):');
    try {
      await fetch(`${API_URL}/api/admin/security/alerts/${id}/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_taken: action || 'Resolved' })
      });
      fetchAlerts();
      setSelectedAlert(null);
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const deleteAlert = async (id) => {
    if (!window.confirm('Удалить алерт?')) return;
    try {
      await fetch(`${API_URL}/api/admin/security/alerts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAlerts();
      setSelectedAlert(null);
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const blockIP = async (ip) => {
    const reason = prompt('Причина блокировки:', 'Suspicious activity');
    if (!reason) return;
    try {
      await fetch(`${API_URL}/api/admin/security/ip-blocklist`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ip_address: ip, reason })
      });
      alert('IP заблокирован!');
    } catch (err) {
      alert('Ошибка: ' + err.message);
    }
  };

  const stats = {
    total: alerts.length,
    unresolved: alerts.filter(a => !a.is_resolved).length,
    high: alerts.filter(a => a.severity === 'high' || a.severity === 'critical').length,
    today: alerts.filter(a => new Date(a.created_at).toDateString() === new Date().toDateString()).length
  };

  const filteredAlerts = alerts.filter(a => {
    if (severityFilter !== 'all' && a.severity !== severityFilter) return false;
    if (statusFilter === 'resolved' && !a.is_resolved) return false;
    if (statusFilter === 'unresolved' && a.is_resolved) return false;
    return true;
  });

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const getSeverity = (s) => SEVERITY_LABELS[s] || { label: s, icon: '❓', color: 'gray' };
  const getType = (t) => TYPE_LABELS[t] || { label: t, icon: '📋' };

  if (loading) return <div className="admin-loading">Загрузка...</div>;

  return (
    <div className="admin-alerts">
      <div className="admin-page-header">
        <h1 className="admin-page-title">🛡️ Оповещения безопасности</h1>
        <button className="admin-btn admin-btn-primary" onClick={fetchAlerts}>🔄</button>
      </div>

      {/* Stats */}
      <div className="alert-stats">
        <div className="alert-stat" onClick={() => { setSeverityFilter('all'); setStatusFilter('all'); }}>
          <span className="alert-stat-value">{stats.total}</span>
          <span className="alert-stat-label">Всего</span>
        </div>
        <div className="alert-stat orange" onClick={() => setStatusFilter('unresolved')}>
          <span className="alert-stat-value">{stats.unresolved}</span>
          <span className="alert-stat-label">Не решено</span>
        </div>
        <div className="alert-stat red" onClick={() => setSeverityFilter('high')}>
          <span className="alert-stat-value">{stats.high}</span>
          <span className="alert-stat-label">Критичных</span>
        </div>
        <div className="alert-stat blue">
          <span className="alert-stat-value">{stats.today}</span>
          <span className="alert-stat-label">Сегодня</span>
        </div>
      </div>

      {/* Filters */}
      <div className="alert-filters">
        <div className="filter-group">
          <label>Статус:</label>
          <div className="filter-buttons">
            <button className={statusFilter === 'all' ? 'active' : ''} onClick={() => setStatusFilter('all')}>📋 Все</button>
            <button className={statusFilter === 'unresolved' ? 'active' : ''} onClick={() => setStatusFilter('unresolved')}>⏳ Не решено</button>
            <button className={statusFilter === 'resolved' ? 'active' : ''} onClick={() => setStatusFilter('resolved')}>✅ Решено</button>
          </div>
        </div>
        <div className="filter-group">
          <label>Уровень:</label>
          <div className="filter-buttons">
            <button className={severityFilter === 'all' ? 'active' : ''} onClick={() => setSeverityFilter('all')}>📋 Все</button>
            {Object.entries(SEVERITY_LABELS).map(([key, val]) => (
              <button key={key} className={severityFilter === key ? 'active' : ''} onClick={() => setSeverityFilter(key)}>
                {val.icon} {val.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="alerts-container">
        <div className="alerts-list">
          {filteredAlerts.length === 0 ? (
            <div className="admin-empty">✅ Нет оповещений</div>
          ) : (
            filteredAlerts.map(alert => {
              const severity = getSeverity(alert.severity);
              const type = getType(alert.type);
              return (
                <div 
                  key={alert.id} 
                  className={`alert-card ${alert.is_resolved ? 'resolved' : ''} ${selectedAlert?.id === alert.id ? 'selected' : ''}`}
                  onClick={() => setSelectedAlert(alert)}
                >
                  <div className="alert-card-header">
                    <span className={`alert-severity ${severity.color}`}>{severity.icon} {severity.label}</span>
                    <span className="alert-type">{type.icon} {type.label}</span>
                    {alert.is_resolved && <span className="alert-resolved-badge">✅ Решено</span>}
                  </div>
                  <h4 className="alert-title">{alert.title}</h4>
                  <p className="alert-desc">{alert.description}</p>
                  <div className="alert-meta">
                    {alert.ip_address && <span>🌐 {alert.ip_address}</span>}
                    {alert.user_email && <span>👤 {alert.user_email}</span>}
                    <span>🕐 {formatDate(alert.created_at)}</span>
                  </div>
                  <div className="alert-actions">
                    {!alert.is_resolved && (
                      <button className="admin-btn admin-btn-sm admin-btn-success" onClick={(e) => { e.stopPropagation(); resolveAlert(alert.id); }}>
                        ✅ Решить
                      </button>
                    )}
                    {alert.ip_address && (
                      <button className="admin-btn admin-btn-sm admin-btn-warning" onClick={(e) => { e.stopPropagation(); blockIP(alert.ip_address); }}>
                        🚫 Блокировать IP
                      </button>
                    )}
                    <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={(e) => { e.stopPropagation(); deleteAlert(alert.id); }}>
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Detail Panel */}
        {selectedAlert && (
          <div className="alert-detail">
            <div className="detail-header">
              <h3>📋 Детали</h3>
              <button className="admin-btn admin-btn-sm" onClick={() => setSelectedAlert(null)}>✕</button>
            </div>
            <div className="detail-body">
              <div className="detail-row"><span>ID:</span><strong>#{selectedAlert.id}</strong></div>
              <div className="detail-row"><span>Тип:</span><strong>{getType(selectedAlert.type).label}</strong></div>
              <div className="detail-row"><span>Уровень:</span><strong>{getSeverity(selectedAlert.severity).label}</strong></div>
              <div className="detail-row"><span>Заголовок:</span><strong>{selectedAlert.title}</strong></div>
              <div className="detail-row"><span>Описание:</span><span>{selectedAlert.description}</span></div>
              {selectedAlert.ip_address && <div className="detail-row"><span>IP:</span><code>{selectedAlert.ip_address}</code></div>}
              {selectedAlert.user_email && <div className="detail-row"><span>Email:</span><code>{selectedAlert.user_email}</code></div>}
              <div className="detail-row"><span>Создан:</span><span>{formatDate(selectedAlert.created_at)}</span></div>
              {selectedAlert.is_resolved && (
                <>
                  <div className="detail-row"><span>Решён:</span><span>{formatDate(selectedAlert.resolved_at)}</span></div>
                  {selectedAlert.action_taken && <div className="detail-row"><span>Действие:</span><span>{selectedAlert.action_taken}</span></div>}
                </>
              )}
              {selectedAlert.metadata && Object.keys(selectedAlert.metadata).length > 0 && (
                <div className="detail-row">
                  <span>Данные:</span>
                  <pre>{JSON.stringify(selectedAlert.metadata, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSecurityAlerts;